import { object, string, mixed, number } from 'yup'
import {
  RULE_ACTION,
  ISpotBiddingRule,
} from 'apps/spot-quote-control-panel/types/spotBiddingConfig'

const spotPremiumSchema = {
  spotPremium: object().when('ruleAction', {
    is: RULE_ACTION.applyPremium,
    then: object().shape({
      amount: number()
        .typeError('Must enter a numeric premium amount.')
        .required('Must enter premium amount.'),
    }),
  }),
}

const geoSchema = {
  geographicCriteria: object()
    .shape({
      value: string().required('Must enter a valid location.'),
    })
    .default(undefined)
    .nullable(),
}

const mileageSchema = {
  mileageCriteria: object()
    .shape({
      rangeStart: string(),
    })
    .default(undefined)
    .nullable(),
}

const contractSchema = {
  contractCriteria: object()
    .shape({
      awardType: string().required('Must select an award type'),
    })
    .default(undefined)
    .nullable(),
}

const numDaysOutSchema = {
  proximalHourCriteria: object()
    .shape(
      {
        rangeStart: string().when('rangeEnd', {
          is: rangeEnd => !rangeEnd,
          then: string().required('Must enter a number of days/hours.'),
        }),
        rangeEnd: string().when('rangeStart', {
          is: rangeStart => !rangeStart,
          then: string().required('Must enter a number of days/hours.'),
        }),
      },
      [['rangeStart', 'rangeEnd']]
    )
    .default(undefined)
    .nullable(),
}

const dateSchema = {
  dateCriteria: object()
    .shape({
      rangeStart: string().required('Must pick a valid date.'),
      rangeEnd: string().required('Must pick a valid date.'),
    })
    .default(undefined)
    .nullable(),
}

const distanceToMarketCenterSchema = {
  distanceToMarketCenterCriteria: object()
    .shape(
      {
        rangeStart: string().when('rangeEnd', {
          is: rangeEnd => !rangeEnd,
          then: string().required('Must enter a number of miles.'),
        }),
        rangeEnd: string().when('rangeStart', {
          is: rangeStart => !rangeStart,
          then: string().required('Must enter a number of miles.'),
        }),
      },
      [['rangeStart', 'rangeEnd']]
    )
    .default(undefined)
    .nullable(),
}

const onlyBidOnQualifierSchema = {
  conditionQualifier: string().when('ruleAction', {
    is: RULE_ACTION.onlyBidOn,
    then: string().required('Must select a qualifier'),
  }),
}

const onlyBidOnConditionSchema = {
  onlyBidOnCriteria: string().when('ruleAction', {
    is: RULE_ACTION.onlyBidOn,
    then: string().required('Must select a condition'),
  }),
}

const ruleSchema = {
  ...spotPremiumSchema,
  ...geoSchema,
  ...mileageSchema,
  ...contractSchema,
  ...distanceToMarketCenterSchema,
  ...numDaysOutSchema,
  ...onlyBidOnQualifierSchema,
  ...onlyBidOnConditionSchema,
  ...dateSchema,
  ruleAction: mixed().oneOf(Object.values(RULE_ACTION)),
}

export interface IValidationErrors {
  ruleAction?: string
  spotPremium?: string
}

export const validateRule = (rule: RecursivePartial<ISpotBiddingRule>) => {
  try {
    return object().shape(ruleSchema).validateSync(rule, { abortEarly: false })
  } catch (errs) {
    const validationMessages = errs.inner.reduce(
      (acc: { [key: string]: string }, { message, path }: { path: string; message: string }) => {
        acc[path.split('.')[0]] = message
        return acc
      },
      {}
    )

    throw validationMessages
  }
}
